// Theme context for white-label customization
import { createContext, useContext, useEffect, useMemo, ReactNode } from 'react';
import type { ThemeConfig } from '../types/config';

// =====================================================
// DEFAULT THEME
// =====================================================

const defaultTheme: Required<ThemeConfig> = {
  // Colors
  primaryColor: '#1a1a1a',
  accentColor: '#2563eb',
  backgroundColor: '#ffffff',
  surfaceColor: '#ffffff',
  textColor: '#1a1a1a',
  textMutedColor: '#666666',
  borderColor: '#e5e5e5',
  errorColor: '#dc2626',
  successColor: '#16a34a',
  
  // Typography
  fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
  fontSize: 16,
  
  // Borders
  borderRadius: '8px',
  buttonRadius: '8px',
  
  // Buttons
  buttonStyle: 'filled',
  
  // Branding
  logoUrl: '',
  logoPosition: 'left',
  logoHeight: 32,
  showPoweredBy: true,
  
  // Layout
  showOrderSummary: true,
  compactMode: false,
};

// =====================================================
// CONTEXT
// =====================================================

interface ThemeContextValue {
  theme: Required<ThemeConfig>;
  cssVariables: Record<string, string>;
}

const ThemeContext = createContext<ThemeContextValue>({
  theme: defaultTheme,
  cssVariables: {},
});

// =====================================================
// PROVIDER
// =====================================================

interface ThemeProviderProps {
  config?: ThemeConfig;
  children: ReactNode;
}

export function ThemeProvider({ config, children }: ThemeProviderProps) {
  // Merge with defaults
  const theme = useMemo<Required<ThemeConfig>>(() => ({
    ...defaultTheme,
    ...config,
  }), [config]);
  
  // Generate CSS variables
  const cssVariables = useMemo(() => ({
    '--sagapay-primary': theme.primaryColor,
    '--sagapay-accent': theme.accentColor,
    '--sagapay-bg': theme.backgroundColor,
    '--sagapay-surface': theme.surfaceColor,
    '--sagapay-text': theme.textColor,
    '--sagapay-text-muted': theme.textMutedColor,
    '--sagapay-border': theme.borderColor,
    '--sagapay-error': theme.errorColor,
    '--sagapay-success': theme.successColor,
    '--sagapay-font': theme.fontFamily,
    '--sagapay-font-size': `${theme.fontSize}px`,
    '--sagapay-radius': theme.borderRadius,
    '--sagapay-button-radius': theme.buttonRadius,
  }), [theme]);
  
  // Apply CSS variables to document root
  useEffect(() => {
    const root = document.documentElement;
    Object.entries(cssVariables).forEach(([key, value]) => {
      root.style.setProperty(key, value);
    });
    
    return () => {
      Object.keys(cssVariables).forEach((key) => {
        root.style.removeProperty(key);
      });
    };
  }, [cssVariables]);
  
  return (
    <ThemeContext.Provider value={{ theme, cssVariables }}>
      {children}
    </ThemeContext.Provider>
  );
}

// =====================================================
// HOOKS
// =====================================================

export function useTheme() {
  return useContext(ThemeContext);
}

export function useThemeConfig() {
  return useContext(ThemeContext).theme;
}

// Helper to check if using custom colors
export function useIsCustomThemed() {
  const { theme } = useTheme();
  return theme.primaryColor !== defaultTheme.primaryColor;
}
